import { useMemo, useState } from "react";
import { FormValue, TaskForm } from "../../../../interfaces/DTO/forms/taskForm";
import { taskFormService } from "../../../../services/forms/taskFormService";

/** a task that will be created, whose producer's form may be prepared now - before the task exists */
export interface PreparedFormItem {
  key: string;
  label: string;
  /** its draft - the form it would get, pre-filled (available=false: no template) */
  draft: () => Promise<TaskForm>;
}

/** an item's form: its draft (null - no template in the system), whether it is prepared now, its values */
export interface PreparedForm {
  loading: boolean;
  draft: TaskForm | null;
  now: boolean; // prepared now in the system (signed with us) - else later, in an external forms system
  values: Record<string, FormValue>;
  problems: string[];
}

const NONE: PreparedForm = { loading: false, draft: null, now: false, values: {}, problems: [] };

/**
 * Preparing producers' forms in a dialog that creates tasks (a beneficiaries update, a new standing order ...):
 * each item's draft - pre-filled from what the task will be - prepared now in the system or later in an external
 * one; the licence holder's signature when none is saved; and, once the tasks exist, saving each form on its task
 * (sent to the client for signature).
 */
export function usePreparedForms() {
  const [forms, setForms] = useState<Record<string, PreparedForm>>({});
  const [agentSignature, setAgentSignature] = useState<string | null>(null);

  /** the items' drafts - the ones already made are kept (with their edits), the others are made now */
  const load = (items: PreparedFormItem[]) => {
    const keep: Record<string, PreparedForm> = {};
    items.forEach((item) => {
      keep[item.key] = forms[item.key] ?? { ...NONE, loading: true };
    });
    setForms(keep);
    items.filter((item) => !forms[item.key]).forEach((item) => {
      item.draft()
        .then((draft) => setForms((prev) => ({
          ...prev,
          [item.key]: draft.available
            ? { loading: false, draft, now: true, values: draft.values, problems: draft.problems }
            : NONE,
        })))
        .catch(() => setForms((prev) => ({ ...prev, [item.key]: NONE })));
    });
  };

  /** the drafts are made again next time (what they were pre-filled from changed) */
  const reset = () => {
    setForms({});
    setAgentSignature(null);
  };

  const setNow = (key: string, now: boolean) => setForms((prev) => ({ ...prev, [key]: { ...prev[key], now } }));

  /** the agent's edits - and what still blocks the form, by the server */
  const setValues = (key: string, values: Record<string, FormValue>) => {
    const versionId = forms[key]?.draft?.template_version_id;
    setForms((prev) => ({ ...prev, [key]: { ...prev[key], values } }));
    if (versionId == null) return;
    taskFormService.checkDraft(versionId, values)
      .then((problems) => setForms((prev) => ({ ...prev, [key]: { ...prev[key], problems } })))
      .catch((): void => undefined);
  };

  const preparedKeys = useMemo(
    () => Object.keys(forms).filter((key) => forms[key].now && forms[key].draft),
    [forms]
  );
  const loading = Object.values(forms).some((f) => f.loading);
  const withProblems = preparedKeys.filter((key) => forms[key].problems.length > 0);
  const needsDrawnSignature = preparedKeys.some(
    (key) => forms[key].draft?.needs_agent_signature && !forms[key].draft?.agent_signed
  );
  // the tasks can be created: every draft made, the ones prepared now complete and the licence holder signed
  const ready = !loading && withProblems.length === 0 && (!needsDrawnSignature || !!agentSignature);

  /**
   * The forms prepared now, saved on the tasks just created (taskIdOf - the item's task, if it was created) and
   * sent to the client for signature. Returns the keys whose form was not saved - prepared later, from the task.
   */
  const saveOnTasks = async (taskIdOf: (key: string) => number | undefined): Promise<string[]> => {
    const failed: string[] = [];
    for (const key of preparedKeys) {
      const form = forms[key];
      const taskId = taskIdOf(key);
      if (taskId == null || form.draft?.template_version_id == null) {
        failed.push(key);
        continue;
      }
      try {
        await taskFormService.savePrepared(taskId, form.draft.template_version_id, form.values, agentSignature);
      } catch (e) {
        console.error("Failed to save the prepared form:", e);
        failed.push(key);
      }
    }
    return failed;
  };

  return {
    forms, load, reset, setNow, setValues, preparedKeys, loading, withProblems, needsDrawnSignature,
    agentSignature, setAgentSignature, ready, saveOnTasks,
  };
}

export type PreparedForms = ReturnType<typeof usePreparedForms>;
